import { captureSentry } from '@/utils/sentryHelper';

import { CaseConfig } from '../types';

export function doesMasterClassQuestionHaveACorrectAnswer(
  caseConfig: CaseConfig,
  slideId: string
): boolean {
  // Trouver la slide
  const slide = caseConfig.slides.find((s) => s.id === slideId);

  if (!slide) {
    console.error(`[doesMasterClassQuestionHaveACorrectAnswer] Slide not found: ${slideId}`);
    captureSentry({
      title: 'Slide not found in correct answer check',
      detail: { slideId },
    });
    return false;
  }

  // Vérifier que c'est une question
  if (slide.type !== 'question_single_choice') {
    console.error(
      `[doesMasterClassQuestionHaveACorrectAnswer] Slide is not a question slide: ${slide.type}`
    );
    captureSentry({
      title: 'Invalid slide type for correct answer check',
      detail: { slideId, slideType: slide.type },
    });
    return false;
  }

  // Récupérer les réponses correctes depuis la questionMap
  const questionMapItem = caseConfig.masterclass!.questionMap[slide.id];

  if (!questionMapItem) {
    console.error(
      `[doesMasterClassQuestionHaveACorrectAnswer] Question map item not found for slide: ${slideId}`
    );
    captureSentry({
      title: 'Question map item not found',
      detail: { slideId },
    });
    return false;
  }

  const { correctAnswerIds } = questionMapItem;

  return correctAnswerIds.length > 0;
}

/**
 * Valide si une ou plusieurs réponses sont correctes pour une question donnée
 *
 * @param caseConfig - Configuration complète du case
 * @param slideId - ID de la slide question
 * @param selectedAnswers - Tableau d'IDs de réponses sélectionnées (pour compatibilité future)
 * @returns boolean - true si la réponse est correcte
 *
 * Note: Actuellement supporte uniquement les questions à choix unique (question_single_choice)
 * La fonction accepte un tableau pour une compatibilité future avec d'autres types de questions
 */
export function isMasterClassAnswerCorrect(
  caseConfig: CaseConfig,
  slideId: string,
  selectedAnswers: string[]
): boolean {
  // Trouver la slide
  const slide = caseConfig.slides.find((s) => s.id === slideId);

  if (!slide) {
    console.error(`[isMasterClassAnswerCorrect] Slide not found: ${slideId}`);
    captureSentry({
      title: 'Slide not found in answer validation',
      detail: { slideId, selectedAnswers },
    });
    return false;
  }

  // Vérifier que c'est une question
  if (slide.type !== 'question_single_choice') {
    console.error(`[isMasterClassAnswerCorrect] Slide is not a question slide: ${slide.type}`);
    captureSentry({
      title: 'Invalid slide type for answer validation',
      detail: { slideId, slideType: slide.type, selectedAnswers },
    });
    return false;
  }

  // Récupérer les réponses correctes depuis la questionMap
  const questionMapItem = caseConfig.masterclass!.questionMap[slide.id];

  if (!questionMapItem) {
    console.error(`[isMasterClassAnswerCorrect] Question map item not found for slide: ${slideId}`);
    captureSentry({
      title: 'Question map item not found',
      detail: { slideId, selectedAnswers },
    });
    return false;
  }

  const { correctAnswerIds } = questionMapItem;

  // Validation pour question à choix unique
  // Note: On vérifie si AU MOINS UNE des réponses sélectionnées est correcte
  // (même si selectedAnswers contient plusieurs éléments par erreur)
  return correctAnswerIds.some((correctId) => selectedAnswers.includes(correctId));
}

/**
 * Version simplifiée pour une seule réponse (cas le plus courant)
 */
export function isAnswerCorrectSimple(
  caseConfig: CaseConfig,
  slideId: string,
  answerId: string
): boolean {
  return isMasterClassAnswerCorrect(caseConfig, slideId, [answerId]);
}
